import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckIcon } from 'lucide-react';
import { FC } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@new/lib';

const checkboxVariants = cva(
  'peer shrink-0 cursor-pointer outline-none transition-all disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'h-5 w-5 rounded-[4px]! border shadow-xs bg-(--neutral-white-NW800) lg:h-[23px] lg:w-[23px]',
        simple:
          'h-4 w-4 rounded-[3px]! border-2 bg-(--neutral-white-NW1000) lg:h-[30px] lg:w-[30px]',
        small:
          'h-4 w-4 rounded-[4px]! border shadow-xs bg-(--neutral-white-NW800) lg:h-[18px] lg:w-[18px]',
      },
    },
    compoundVariants: [
      {
        variant: ['default', 'small'],
        className: 'data-[state=checked]:border-(--neutral-black-NB1000)',
      },
    ],
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface CheckboxProps extends VariantProps<typeof checkboxVariants> {
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

const indicatorVariants = cva('flex items-center justify-center text-(--neutral-white-NW1000)!', {
  variants: {
    variant: {
      default: '',
      simple: '',
      small: '',
    },
  },
});

const iconVariants = cva('shrink-0', {
  variants: {
    variant: {
      default: 'h-4 w-4',
      simple: 'h-3 w-3 lg:h-4 lg:w-4',
      small: 'h-3 w-3',
    },
  },
});

const ICON_STROKE_WIDTH = {
  default: 2.5,
  simple: 2,
  small: 2.5,
} as const;

export const Checkbox: FC<CheckboxProps> = ({ value, onChange, variant = 'default', disabled }) => (
  <CheckboxPrimitive.Root
    checked={value}
    disabled={disabled}
    onCheckedChange={(checked) => onChange(Boolean(checked))}
    data-slot="checkbox"
    className={cn(
      checkboxVariants({ variant }),
      'data-[state=checked]:bg-(--neutral-black-NB1000)'
    )}
  >
    <CheckboxPrimitive.Indicator
      data-slot="checkbox-indicator"
      className={cn(indicatorVariants({ variant }))}
    >
      <CheckIcon
        className={cn(iconVariants({ variant }))}
        strokeWidth={ICON_STROKE_WIDTH[variant ?? 'default']}
      />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
);
